import * as ReactDOM from "react-dom";
import * as React from "react";

const { Component } = React;

import { BrowserRouter, Route, NavLink, Routes, useNavigate, useLocation } from "react-router-dom";

import { GetCurrentLoggedInUser, Account } from "./account/AccountStore";

import HomeIndex from "./home/HomeIndex";
import Profile from "./account/Profile";
import ProfileData from "./account/ProfileData";
import ProfileScadsysData from "./account/ProfileScadsysData";
import ProfileMultiFactor from "./account/ProfileMultiFactor";
import ProfileSecurity from "./account/ProfileSecurity";
import ProfileNotifications from "./account/ProfileNotifications";
import ApproveLogin from "./account/ApproveLogin";

import Loader from "./general/Loader";
import OrganizationIndex, { Tabs } from "./organizations/Index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UsersIndex } from "./users/Index";
import { AdminsIndex } from "./admins/AdminsIndex";
import { DataMaintenanceIndex } from "./dataMaintenance/DataMaintenanceIndex";
import { GetContext } from "./general/ApplicationContext";
import { Organization } from "./users/UserStore";
import { GroupsIndex } from "./groups/Index";
import { OverviewIndex } from "./overview/Index";
import GuidesIndex from "./guides/GuidesIndex";
import GuidesCreate from "./guides/GuidesCreate";
import GuidesEdit from "./guides/GuidesEdit";
import ScadsysIndex from "./scadsys/ScadsysIndex";
import { Modal, Button } from "./components/Modal";
import CreateOrganization from "./organizations/Create";

const OrganizationIndexWrapper = () => {
    const navigate = useNavigate();
    const location = useLocation();

    return <OrganizationIndex navigate={navigate} location={location} />;
};

function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/*" element={<Home></Home>} />
            </Routes>
        </BrowserRouter>
    );
}

const Home = () => {
    const [user, setUser] = React.useState(new Account({}));
    const [loading, setLoading] = React.useState(true);
    const location = useLocation();
    const [organization, setOrganization] = React.useState({} as Organization);
    const [sidebarOpen, setSidebarOpen] = React.useState(window.screen.width >= 768);
    const [showCreateOrgModal, setShowCreateOrgModal] = React.useState(false);

    const [logoImage, setLogoImage] = React.useState("/static/branding/images/logo");

    React.useEffect(() => {
        GetCurrentLoggedInUser()
            .then((user) => {
                setUser(user);
                setLoading(false);
            })
            .catch(() => {
                window.location.href = "/Account/Login";
            });

        GetContext((ctx) => {
            setLogoImage("/static/branding/images/logo?brandingId=" + (ctx.currentOrganization?.id || ""));
            setOrganization(ctx.currentOrganization);
        });
    }, []);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const toggleCreateOrgModal = () => {
        setShowCreateOrgModal(!showCreateOrgModal);
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar */}
            <aside
                className={`${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 shadow-2xl`}
            >
                {/* Logo Section */}
                <div className="flex items-center justify-center h-20 border-b border-gray-700/50 px-6 bg-white m-4 rounded-lg">
                    <a href="/" style={{ textDecoration: "none" }} className="flex items-center no-underline">
                        <img src={logoImage} alt={organization.name} className="h-8 max-w-[180px] object-contain" />
                    </a>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
                    {user && user.id && (
                        <>
                            {/* Admin Navigation */}
                            {user.isAdmin() && (
                                <>
                                    <NavItem to="/organizations" icon={["fad", "building"]} label="Organizations" location={location} />
                                    {location.pathname.startsWith("/organizations") && (
                                        <div className="ml-6 space-y-0.5 mt-1 mb-2">
                                            {Tabs.map((t) => (
                                                <SubNavItem key={t.route} to={t.route} icon={t.icon} label={t.name} />
                                            ))}
                                        </div>
                                    )}

                                    <NavItem to="/users" icon={["fad", "users"]} label="Users" location={location} />
                                    <NavItem to="/groups" icon={["fad", "users-cog"]} label="Groups" location={location} />
                                    <NavItem to="/overview" icon={["fad", "chart-pie"]} label="Overview" location={location} />
                                    <NavItem to="/admins" icon={["fad", "user-gear"]} label="Admins" location={location} />
                                    {user.isSuperAdmin() && <NavItem to="/data-maintenance" icon={["fad", "database"]} label="Data Maintenance" location={location} />}
                                    <NavItem to="/scadsys" icon={["fad", "server"]} label="Scadsys" location={location} />
                                    <NavItem to="/guides" icon={["fad", "book"]} label="Guides" location={location} />
                                </>
                            )}

                            {/* Non-Admin Navigation */}
                            {!user.isAdmin() && (
                                <>
                                    {user.hasPermission("manageOrganizations") ||
                                        (Tabs.some((t) => user.hasPermission(t.permission)) && (
                                            <>
                                                <NavItem to="/organizations" icon={["fad", "building"]} label="Organizations" location={location} />
                                                {location.pathname.startsWith("/organizations") && (
                                                    <div className="ml-6 space-y-0.5 mt-1 mb-2">
                                                        {Tabs.filter((t) => user.hasPermission(t.permission)).map((t) => (
                                                            <SubNavItem key={t.route} to={t.route} icon={t.icon} label={t.name} />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ))}
                                    {user.hasPermission("searchUsers") && <NavItem to="/users" icon={["fad", "users"]} label="Users" location={location} />}
                                    {user.hasPermission("manageOrganizationGroups") && <NavItem to="/groups" icon={["fad", "users-cog"]} label="Groups" location={location} />}
                                    {user.hasPermission("manageOrganizations") && <NavItem to="/overview" icon={["fad", "chart-pie"]} label="Overview" location={location} />}
                                    {user.isStaff() && <NavItem to="/guides" icon={["fad", "book"]} label="Guides" location={location} />}
                                </>
                            )}

                            {/* Add Organization (for admins) */}
                            {user.isAdmin() && (
                                <div className="px-3">
                                    <button
                                        onClick={toggleCreateOrgModal}
                                        style={{ textDecoration: "none" }}
                                        className="w-full flex items-center px-3 py-2.5 text-gray-400 hover:bg-blue-600/20 hover:text-blue-400 rounded-lg transition-all duration-200 group no-underline mb-3"
                                    >
                                        <FontAwesomeIcon icon={["fad", "plus"]} className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform text-gray-500 group-hover:text-blue-400" />
                                        <span className="font-medium text-base">Add Organization</span>
                                    </button>
                                </div>
                            )}

                            {/* Sign Out */}
                            <div className="px-3">
                                <a
                                    href="/Account/LogOut"
                                    style={{ textDecoration: "none" }}
                                    className="flex items-center px-3 py-2.5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-all duration-200 group no-underline"
                                >
                                    <FontAwesomeIcon icon={["fad", "lock"]} className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform text-gray-500 group-hover:text-red-400" />
                                    <span className="font-medium text-base">Sign out</span>
                                </a>
                            </div>
                        </>
                    )}

                    {!user.id && (
                        <a
                            href="/Account/LogIn"
                            style={{ textDecoration: "none" }}
                            className="flex items-center px-3 py-2.5 text-gray-400 hover:bg-blue-600/20 hover:text-blue-400 rounded-lg transition-all duration-200 group no-underline"
                        >
                            <FontAwesomeIcon icon={["fad", "unlock"]} className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform text-gray-500 group-hover:text-blue-400" />
                            <span className="font-medium text-base">Sign in</span>
                        </a>
                    )}
                </nav>
            </aside>

            {/* Mobile Overlay */}
            {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={toggleSidebar}></div>}

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header */}
                <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
                    <button onClick={toggleSidebar} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Toggle menu">
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <img src={logoImage} alt={organization.name} className="h-6 max-w-[140px] object-contain" />
                    <div className="w-10"></div> {/* Spacer for centering logo */}
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto bg-gray-50">
                    <div className="">
                        {loading && (
                            <div className="flex items-center justify-center h-96">
                                <Loader />
                            </div>
                        )}

                        {!loading && (
                            <>
                                {user && user.id && (
                                    <Routes>
                                        <Route path="/profile/security" element={<ProfileSecurity />} />
                                        <Route path="/profile/mfa" element={<ProfileMultiFactor />} />
                                        <Route path="/profile/data" element={<ProfileData />} />
                                        <Route path="/profile/scadsys" element={<ProfileScadsysData />} />
                                        <Route path="/profile/notifications" element={<ProfileNotifications />} />
                                        <Route path="/profile" element={<Profile />} />
                                        <Route path="/approve-login/:id" element={<ApproveLogin />} />
                                        <Route path="/organizations/*" element={<OrganizationIndexWrapper />} />
                                        <Route path="/overview" element={<OverviewIndex />} />
                                        <Route path="/users" element={<UsersIndex />} />
                                        <Route path="/groups" element={<GroupsIndex />} />
                                        <Route path="/admins" element={<AdminsIndex />} />
                                        <Route path="/data-maintenance" element={<DataMaintenanceIndex />} />
                                        <Route path="/scadsys" element={<ScadsysIndex />} />
                                        <Route path="/guides" element={<GuidesIndex />} />
                                        <Route path="/guides/create" element={<GuidesCreate />} />
                                        <Route path="/guides/:id" element={<GuidesEdit />} />
                                        <Route path="/" element={<HomeIndex />} />
                                    </Routes>
                                )}

                                {!user.id && (
                                    <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
                                        <a href="/account/login" className="group">
                                            <img className="max-w-md w-full hover:scale-105 transition-transform duration-300" src="/images/cloud-splash.png" alt="Login" />
                                        </a>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* Add Organization Modal */}
            <Modal isOpen={showCreateOrgModal} onClose={toggleCreateOrgModal} title="Add Organization" icon={["fad", "building"]} iconColor="text-blue-600">
                <div className="p-6">
                    <CreateOrganization></CreateOrganization>
                </div>
                <div className="flex justify-end items-center space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <Button variant="secondary" onClick={toggleCreateOrgModal}>
                        Done
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

// Navigation Item Component
const NavItem = ({ to, icon, label, location }: { to: string; icon: any; label: string; location: any }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + "/");

    return (
        <NavLink
            to={to}
            style={{ textDecoration: "none" }}
            className={`flex items-center px-3 py-2.5 rounded-lg transition-all duration-200 group relative no-underline ${
                isActive ? "bg-blue-600 text-white shadow-md" : "text-gray-300 hover:bg-gray-800/50 hover:text-gray-200"
            }`}
        >
            <FontAwesomeIcon icon={icon} className={`w-5 h-5 mr-3 transition-all duration-200 group-hover:scale-110 ${isActive ? "text-white" : "text-gray-500 group-hover:text-blue-400"}`} />
            <span className="font-medium text-base">{label}</span>
            {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>}
        </NavLink>
    );
};

// Sub Navigation Item Component
const SubNavItem = ({ to, icon, label }: { to: string; icon: any; label: string }) => {
    return (
        <NavLink
            to={to}
            style={{ textDecoration: "none" }}
            className={({ isActive }) =>
                `flex items-center px-3 py-2 rounded-md text-sm transition-all duration-200 group no-underline ${
                    isActive ? "bg-blue-600/30 text-blue-300 font-medium" : "text-gray-300 hover:bg-gray-800/40 hover:text-gray-300"
                }`
            }
        >
            {/* @ts-ignore */}
            <FontAwesomeIcon icon={icon} className={`w-4 h-4 mr-2.5 transition-colors duration-200`} />
            <span>{label}</span>
        </NavLink>
    );
};

ReactDOM.render(React.createElement(App, {}, null), document.getElementById("home"));
